import React, { useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { formatMoney } from '../../Shared/Formatters';

/**
 * N-series time-series chart with chart-type and normalize toggles.
 *
 * `alignPeriods` re-keys each series to its own bucket index (1..N) instead
 * of an absolute date so different years overlap on the same x-axis. Tooltip
 * still shows the original bucket label per series.
 *
 * `normalize` modes:
 *   - 'absolute' — raw values (default)
 *   - 'index100' — each series scaled so its first non-zero bucket = 100
 *   - 'percent'  — at each bucket, value as % of bucket-total across series
 *
 * Chart type 'stacked' / 'stacked100' map to bar chart with stacked / 100%.
 */
export default function MultiSeriesChart({
    seriesData,
    metric,
    chartType = 'line',
    normalize = 'absolute',
    alignPeriods = false,
    palette,
}) {
    const { t } = useTranslation();
    const fld = metricFieldKey(metric);

    const { categories, apexSeries, originalLabels } = useMemo(
        () => transform(seriesData, fld, alignPeriods, normalize),
        [seriesData, fld, alignPeriods, normalize]
    );

    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const stacked = chartType === 'stacked' || chartType === 'stacked100';
    const apexType = stacked ? 'bar' : (chartType === 'area' ? 'area' : chartType === 'bar' ? 'bar' : 'line');

    const safeFormatMoney = (v) => (v == null || isNaN(v)) ? '' : formatMoney(v);
    const yFmt = normalize === 'percent'
        ? (v) => (v == null || isNaN(v)) ? '' : `${Number(v).toFixed(1)}%`
        : normalize === 'index100'
            ? (v) => (v == null || isNaN(v)) ? '' : Number(v).toFixed(0)
            : safeFormatMoney;

    const chartOpts = {
        type: apexType,
        toolbar: { show: false },
        fontFamily: 'Inter, system-ui',
        animations: { enabled: true, speed: 250 },
    };
    if (stacked) {
        chartOpts.stacked = true;
        chartOpts.stackType = chartType === 'stacked100' ? '100%' : 'normal';
    }

    const tooltipOpts = {
        enabled: true,
        theme: isDark ? 'dark' : 'light',
        shared: true,
        intersect: false,
        y: { formatter: yFmt, title: { formatter: (n) => n } },
        x: { show: true },
        marker: { show: true },
        fixed: { enabled: false },
    };
    if (alignPeriods) {
        tooltipOpts.x = {
            show: true,
            formatter: (val, opts) => {
                const bucketIdx = (opts && opts.dataPointIndex) || 0;
                const seriesIdx = (opts && opts.seriesIndex) || 0;
                const orig = originalLabels[seriesIdx] && originalLabels[seriesIdx][bucketIdx];
                return orig ? `${val} (${orig})` : String(val);
            },
        };
    }

    const options = {
        chart: chartOpts,
        colors: palette,
        stroke: (apexType === 'line' || apexType === 'area')
            ? { width: 2, curve: 'smooth' }
            : { width: 0 },
        plotOptions: apexType === 'bar'
            ? { bar: { columnWidth: '60%', borderRadius: 4 } }
            : {},
        grid: { borderColor: 'var(--v3-chart-grid)', strokeDashArray: 4 },
        xaxis: {
            categories,
            labels: { style: { colors: 'var(--v3-text-muted)', fontSize: '10px' } },
        },
        yaxis: {
            labels: { formatter: yFmt, style: { colors: 'var(--v3-text-muted)' } },
        },
        tooltip: tooltipOpts,
        dataLabels: { enabled: false },
        legend: { show: true, position: 'top', labels: { colors: 'var(--v3-text-muted)' } },
    };
    if (apexType === 'area') {
        options.fill = { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0.02 } };
    }

    if (apexSeries.length === 0 || categories.length === 0) {
        return <div className="v3-text-muted" style={{ padding: 32, textAlign: 'center', fontSize: 13 }}>{t('No data for the selected range.')}</div>;
    }

    return (
        <ChartErrorBoundary>
            <Chart
                key={`${apexType}-${stacked}-${normalize}-${alignPeriods}`}
                type={apexType}
                options={options}
                series={apexSeries}
                height={380}
            />
        </ChartErrorBoundary>
    );
}

class ChartErrorBoundaryBase extends React.Component {
    constructor(props) { super(props); this.state = { err: null }; }
    static getDerivedStateFromError(err) { return { err }; }
    componentDidCatch(err) { console.error('[MultiSeriesChart] render error:', err); }
    render() {
        if (this.state.err) {
            return (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--v3-text-muted)', fontSize: 13 }}>
                    {this.props.t('Chart render failed. Try a different chart type or fewer series.')}
                </div>
            );
        }
        return this.props.children;
    }
}

const ChartErrorBoundary = withTranslation()(ChartErrorBoundaryBase);

function metricFieldKey(metric) {
    if (metric === 'transactions') return 'trans_count';
    if (metric === 'revenue') return 'revenue_share_amount';
    return 'sales';
}

function transform(seriesData, fld, alignPeriods, normalize) {
    if (!Array.isArray(seriesData) || seriesData.length === 0) {
        return { categories: [], apexSeries: [], originalLabels: [] };
    }

    let categories;
    let perSeriesValues;     // [seriesIdx][bucketIdx] = value
    const originalLabels = []; // [seriesIdx][bucketIdx] = bucket key shown in tooltip

    if (alignPeriods) {
        const lengths = seriesData.map(s => (s.series || []).length);
        const maxLen = Math.max(0, ...lengths);
        categories = Array.from({ length: maxLen }, (_, i) => `#${i + 1}`);
        perSeriesValues = seriesData.map((s) => {
            const rows = s.series || [];
            const labels = [];
            const arr = Array.from({ length: maxLen }, (_, i) => {
                const row = rows[i];
                labels.push(row ? String(row._id) : '');
                return row ? (row[fld] || 0) : null;
            });
            originalLabels.push(labels);
            return arr;
        });
    } else {
        const set = new Set();
        seriesData.forEach(s => (s.series || []).forEach(r => set.add(String(r._id))));
        categories = [...set].sort();
        perSeriesValues = seriesData.map((s) => {
            const map = new Map();
            (s.series || []).forEach(r => map.set(String(r._id), r[fld] || 0));
            const labels = categories.map(k => k);
            originalLabels.push(labels);
            return categories.map(k => (map.has(k) ? map.get(k) : null));
        });
    }

    if (normalize === 'index100') {
        perSeriesValues = perSeriesValues.map(arr => {
            const base = arr.find(v => v != null && v !== 0);
            if (!base) return arr.map(v => (v == null ? null : 0));
            return arr.map(v => (v == null ? null : (v / base) * 100));
        });
    } else if (normalize === 'percent') {
        const len = categories.length;
        for (let bi = 0; bi < len; bi++) {
            let sum = 0;
            for (let si = 0; si < perSeriesValues.length; si++) sum += (perSeriesValues[si][bi] || 0);
            for (let si = 0; si < perSeriesValues.length; si++) {
                const v = perSeriesValues[si][bi];
                perSeriesValues[si][bi] = sum > 0 && v != null ? (v / sum) * 100 : 0;
            }
        }
    }

    const apexSeries = seriesData.map((s, i) => ({
        name: seriesName(s),
        data: perSeriesValues[i],
    }));

    return { categories, apexSeries, originalLabels };
}

function seriesName(s) {
    if (s.label) return s.label;
    if (s.dateRange) return `${s.type}: ${s.id} (${s.dateRange.from})`;
    return `${s.type}: ${s.id}`;
}
